import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, Clock, Activity, Award, AlertTriangle, RefreshCw, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const trialColor = (status, days) => {
  if (status === 'expired') return 'text-red-400 bg-red-500/10 border-red-500/30';
  if (status === 'active' && days !== null && days <= 7) return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
  if (status === 'active') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
  return 'text-zinc-400 bg-zinc-700/20 border-zinc-700/40';
};

export default function TesterActivityPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const r = await fetch(`${API_URL}/api/auth/admin/tester-activity`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.status === 403) { setErr('Admin access required'); return; }
      if (!r.ok) { setErr(`Server returned ${r.status}`); return; }
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Failed to load tester activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Gate: only admins see this panel.
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="tester-activity-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-400" />
          <h3 className="text-base font-bold text-white font-['Outfit']">Tester Activity</h3>
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500">
            // Admin
          </span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="tester-activity-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {!err && data && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <SummaryTile icon={Users}    label="Total testers"    value={data.total_testers}   color="text-cyan-400" />
            <SummaryTile icon={Clock}    label="Trial accounts"   value={data.trial_testers}   color="text-amber-400" />
            <SummaryTile icon={Award}    label="Active trials"    value={data.active_trials}   color="text-emerald-400" />
            <SummaryTile icon={AlertTriangle} label="Expired trials" value={data.expired_trials} color="text-red-400" />
          </div>

          {/* Tester table */}
          <div className="overflow-x-auto rounded-sm border border-[#27272A]">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">
                <tr>
                  <th className="text-left px-3 py-2">Tester</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Logins</th>
                  <th className="text-left px-3 py-2">Last login</th>
                  <th className="text-right px-3 py-2">AI msgs</th>
                  <th className="text-right px-3 py-2">Datalogs</th>
                  <th className="text-right px-3 py-2">Tunes</th>
                  <th className="text-right px-3 py-2">Flashes</th>
                  <th className="text-left px-3 py-2">Vehicles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1a1a1a]">
                {data.rows.length === 0 && (
                  <tr><td colSpan="9" className="text-center text-zinc-500 py-6">No testers yet.</td></tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.user_id} className="hover:bg-[#171717] transition-colors" data-testid={`tester-row-${r.email}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-white font-medium truncate max-w-[200px]">{r.name}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{r.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {r.is_trial ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider ${trialColor(r.trial_status, r.days_remaining)}`}>
                          {r.trial_status === 'active' ? `Trial · ${r.days_remaining}d` : r.trial_status === 'expired' ? 'Expired' : 'Trial'}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider">
                          Beta
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{r.login_count}</td>
                    <td className="px-3 py-2.5 text-zinc-400">{fmtDate(r.last_login_at)}</td>
                    <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{r.ai_chat_count}</td>
                    <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{r.datalog_count}</td>
                    <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{r.tunes_generated}</td>
                    <td className="px-3 py-2.5 text-right text-white font-['JetBrains_Mono']">{r.flash_count}</td>
                    <td className="px-3 py-2.5 text-zinc-400 font-['JetBrains_Mono']">{r.vehicles_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] text-zinc-500 mt-3 font-['JetBrains_Mono']">
            Trial testers seeded via env var TRIAL_BETA_TESTER_EMAILS · default password set by TRIAL_BETA_PASSWORD · trial window TRIAL_BETA_DAYS (default 30).
          </p>
        </>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3 flex items-center gap-3">
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <div className="text-2xl font-bold text-white font-['JetBrains_Mono']">{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">{label}</div>
      </div>
    </div>
  );
}
